import type { KkmServerMappedReceiptPlan } from '@/entities/fiscalization';
import type { KkmExecutionResult, KkmHandshakeReady } from '@/shared/api';

import { getGraphQLErrorCode } from '@/shared/lib';
import {
  type CompleteOrderFiscalSettlementInput,
  type OrderFieldsFragment,
  type OrderFiscalExecutionFieldsFragment,
  OrderFiscalFailureKind,
  OrderFiscalKkmCommandStatus,
  OrderFiscalOperationStatus,
  OrderFiscalRecoveryAction,
  OrderFiscalSettlementResultOutcome,
  OrderStatus,
  type PrepareOrderFiscalSettlementInput,
} from '@shared/api/graphql/gql/graphql';

import type { OrderFiscalizationPhase, OrderFiscalizationState } from './order-fiscalization-state';
import type {
  OrderFiscalizationMutationResult,
  OrderFiscalizationControllerDependencies,
} from './order-fiscalization-types';

import { executeKkmSafely, isProvenNotRun } from './execute-kkm-safely';
import { FiscalOperationController } from './fiscal-operation-controller';
import { FiscalPaymentReview } from './fiscal-payment-review';
import { orderFiscalEquipmentVerification, validateReceiptMarking } from './order-fiscal-equipment-verification';
import {
  toBankEvidenceInput,
  isReceiptRetryAllowed,
  mapFiscalExecutionReceipt,
  identity,
  requireReadyHandshake,
  assertLiveKktReady,
  assertExecutionTarget,
  commandStatus,
  fiscalResultErrorFields,
  markingRefusalMessage,
  resultOperatorMessage,
  fiscalizedResult,
  ShiftReadinessError,
} from './order-fiscal-result';
import { RESETTABLE_PHASES, displayFiscalizationAmount } from './order-fiscalization-state';

export class OrderFiscalizationController extends FiscalOperationController<
  OrderFiscalExecutionFieldsFragment,
  CompleteOrderFiscalSettlementInput
> {
  private activeTerminalKey: string | null = null;

  private readonly dependencies: OrderFiscalizationControllerDependencies;

  private readonly paymentCancellation: FiscalPaymentReview;

  private unownedCodes: string[] = [];

  public constructor(dependencies: OrderFiscalizationControllerDependencies) {
    super(
      dependencies.adapter,
      (state) => dependencies.onState?.(state),
      {
        captured:
          'Деньги получены, чек не подтверждён. Нажмите «Напечатать чек»: Mors проверит кассу и напечатает чек, если его нет.',
        unknown:
          'Непонятно, вышел ли чек. Нажмите «Напечатать чек»: Mors проверит кассу и напечатает чек, если его нет.',
        question: 'Касса не дала прочитать документы. Посмотрите на кассу: вышел ли чек?',
        finalizing: 'Чек напечатан. Сохраняем оплату.',
        pendingSync: 'Чек у клиента на руках, но оплата не записалась. Нажмите «Сохранить оплату».',
        invalidResult: 'Касса ответила непонятно. Откройте запись заново.',
        retry: 'Закончилась чековая бумага. Оплата получена. Замените рулон и нажмите «Повторить печать».',
        failedPrevious: 'Предыдущая попытка закрыта. Можно вернуться к оплате.',
      },
      dependencies,
    );
    this.dependencies = dependencies;
    this.paymentCancellation = new FiscalPaymentReview({
      adapter: dependencies.adapter,
      services: dependencies.services,
      storage: dependencies.storage,
      handshakeBoundKkt: (deviceId) => this.handshakeBoundKkt(deviceId),
      setRetryableExecution: (execution) => {
        this.retryableExecution = execution;
      },
      transition: (phase, message, execution) => this.transition(phase, message, execution),
    });
  }

  protected override resetContext(): void {
    this.paymentCancellation.review = null;
    this.paymentCancellation.cancelledOrderStatus = null;
    this.activeTerminalKey = null;
    this.unownedCodes = [];
  }

  public async settle(
    input: Omit<PrepareOrderFiscalSettlementInput, 'browserWorkstationId' | 'kktDeviceId' | 'kkt' | 'terminal'>,
  ): Promise<OrderFieldsFragment | null> {
    if (this.busy) return null;
    this.busy = true;
    this.unownedCodes = [];
    this.paymentCancellation.review = null;
    this.retryableExecution = null;

    try {
      this.transition('checking_readiness');
      const handshake = await this.handshakeBoundKkt(this.dependencies.expectedKktDeviceId?.());
      const ready = requireReadyHandshake(handshake);
      assertLiveKktReady(ready.kkt, this.dependencies.timezone?.());
      this.liveKkt.capture(ready);

      this.transition('preparing');
      const execution = await this.dependencies.services.prepare({
        ...input,
        kktDeviceId: ready.kktDevice.idDevice,
        ...orderFiscalEquipmentVerification(ready),
      });
      this.dependencies.storage.addPendingOperation(execution.operation.id, execution.operation.order_id);
      assertExecutionTarget(execution, ready);
      return await this.runPrepared(execution);
    } catch (error) {
      if (this.stateValue.phase === 'preparing' && getGraphQLErrorCode(error) === 'advances_changed') {
        this.transition('idle', 'Авансы изменились. Проверьте обновлённую сумму перед оплатой.');
        throw error;
      }
      this.failSettlement(error);
      return null;
    } finally {
      this.busy = false;
    }
  }

  protected override async recoverOrder(
    execution: OrderFiscalExecutionFieldsFragment | null,
    orderId: string,
    cycle: boolean,
  ): Promise<OrderFieldsFragment | null> {
    if (execution === null) {
      this.dependencies.storage.clearPendingOrder(orderId);
      if (this.paymentCancellation.review?.execution.operation.order_id === orderId)
        return await this.paymentCancellation.refreshReviewedOrder(orderId);
      if (this.paymentCancellation.review !== null) {
        this.paymentCancellation.review = null;
        this.transition('idle');
      }
      return null;
    }
    if (
      this.paymentCancellation.review !== null &&
      (this.paymentCancellation.review.execution.operation.id !== execution.operation.id ||
        execution.next_action !== OrderFiscalRecoveryAction.CancelPayment)
    ) {
      this.paymentCancellation.review = null;
    }

    this.dependencies.storage.addPendingOperation(execution.operation.id, execution.operation.order_id);
    return await this.continueRecovery(execution, cycle);
  }

  /** Следующий шаг операции по `next_action` сервера: при восстановлении и при повторе `prepare`. */
  private async continueRecovery(
    execution: OrderFiscalExecutionFieldsFragment,
    cycle: boolean,
  ): Promise<OrderFieldsFragment | null> {
    const receiptStep = this.recoverReceiptStep(execution, cycle);
    if (receiptStep !== undefined) return receiptStep;

    if (execution.next_action === OrderFiscalRecoveryAction.CancelPayment) {
      const reviewed = this.paymentCancellation.review?.execution.operation.id === execution.operation.id;
      this.paymentCancellation.showUnconfirmedPayment(
        execution,
        (reviewed ? this.paymentCancellation.review?.technicalDetails : null) ??
          execution.operation.error_message ??
          null,
        reviewed && this.stateValue.phase === 'payment_unconfirmed' ? this.stateValue.message : null,
      );
      return null;
    }

    await this.captureLiveIdentity(execution);

    if (execution.next_action === OrderFiscalRecoveryAction.StartPayment) {
      if (cycle) {
        await this.failKnownPrePrint(execution, new Error('Оплата не начиналась: смена закрывается.'), 'shift_closing');
        this.transition('failed_before_print', 'Оплата не начиналась. Деньги не списаны.', execution);
        return null;
      }
      return this.runPrepared(execution);
    }

    if (execution.next_action === OrderFiscalRecoveryAction.PrintReceipt) {
      return this.printAndFinalize(execution);
    }

    if (execution.next_action === OrderFiscalRecoveryAction.PollResult) {
      return this.pollAndResume(execution);
    }

    return null;
  }

  protected override async answerReceiptFor(
    execution: OrderFiscalExecutionFieldsFragment,
    printed: boolean,
  ): Promise<OrderFieldsFragment | null> {
    const result = await this.dependencies.services.answerReceiptPrinted({
      ...identity(execution, execution.execution_target.kkt_device_id),
      printed,
    });
    return await this.continueAfterReceiptDecision(result);
  }

  public async cancelPayment(): Promise<boolean> {
    if (!this.stateValue.cancellable || this.activeTerminalKey === null) return false;

    try {
      await this.dependencies.adapter.cancelActiveOperation(this.activeTerminalKey);
      return true;
    } catch (error) {
      this.transition('waiting_payment', this.errorMessage(error));
      return false;
    }
  }

  /**
   * Отмена платежа на терминале: клиент прикладывает карту или телефон. Если деньги
   * списались, терминал их вернёт; если нет — ответит, что отменять нечего.
   */
  public async cancelUnconfirmedPayment(): Promise<OrderFieldsFragment | null> {
    const origin = this.paymentCancellation.review?.execution ?? this.retryableExecution;
    if (this.busy || origin === null || !this.stateValue.canCancelPayment) return null;
    this.busy = true;
    try {
      return await this.paymentCancellation.cancelUnconfirmedPayment(origin, this.paymentCancellation.review === null);
    } finally {
      this.busy = false;
    }
  }

  /** Карту приложить нельзя или клиент платил по СБП: платёж закрывается без команды терминалу. */
  public async cancelWithoutCheck(): Promise<OrderFieldsFragment | null> {
    const execution = this.paymentCancellation.review?.execution;
    if (this.busy || execution === undefined || this.stateValue.phase !== 'payment_unconfirmed') return null;
    this.busy = true;
    try {
      return await this.paymentCancellation.cancelWithoutCheck(execution);
    } finally {
      this.busy = false;
    }
  }

  /**
   * Чек операции ищется в документах ФН. ФН заменён — читать нечего, сервер считает
   * чек непробитым. Касса не отвечает — деньги получены, чек подождёт. ФН не прочитан —
   * вопрос «Чек вышел?»; в цикле смены (`ask = false`) вопроса нет, операция ждёт.
   */
  protected override async resolveReceiptFor(
    execution: OrderFiscalExecutionFieldsFragment,
    ask: boolean,
  ): Promise<OrderFieldsFragment | null> {
    const read = await this.readReceiptDocuments(execution);
    if (read.kind === 'unavailable') {
      this.transition('kkt_unavailable', 'Деньги получены. Чек напечатаем, когда касса ответит.', execution);
      return null;
    }
    if (read.kind === 'unreadable') {
      console.warn('[OrderFiscalizationController.receipt-unresolved]', { operationId: execution.operation.id });
      if (!ask) {
        this.showResolveReceipt(execution);
        return null;
      }
      this.showReceiptQuestion(execution);
      return null;
    }

    const resolved = await this.dependencies.services.resolveReceipt({
      ...identity(execution, execution.execution_target.kkt_device_id),
      ...read.input,
    });

    return this.continueAfterReceiptDecision(resolved);
  }

  /**
   * Решение по чеку принято: чек найден или печатается заново. Наличные без чека
   * сервер считает полученными: заказ оплачен, чек допечатывает повтор печати.
   */
  private async continueAfterReceiptDecision(
    result: OrderFiscalizationMutationResult,
  ): Promise<OrderFieldsFragment | null> {
    const execution = result.execution;
    this.resolvableExecution = null;
    this.receiptQuestionOpen = false;

    if (execution.operation.status === OrderFiscalOperationStatus.Completed) {
      this.dependencies.storage.clearPendingOperation(execution.operation.id);
      this.transition('completed', null, execution);
      return result.order;
    }

    if (isReceiptRetryAllowed(execution)) {
      return this.retryAndPrint(execution);
    }

    if (execution.operation.status === OrderFiscalOperationStatus.Failed) {
      this.dependencies.storage.clearPendingOperation(execution.operation.id);
      this.transition('failed_before_print', 'Чека нет, деньги не получены. Можно вернуться к оплате.', execution);
      return null;
    }

    this.showResolveReceipt(execution);
    return null;
  }

  /** Сбой до prepare или на нём: деньги не двигались. Позже исход печати неясен. */
  private failSettlement(error: unknown): void {
    if (this.stateValue.phase === 'checking_readiness' || this.stateValue.phase === 'preparing') {
      this.transition(
        'failed_before_print',
        this.errorMessage(error),
        undefined,
        false,
        error instanceof ShiftReadinessError ? error.action : null,
      );
      return;
    }
    this.activeTerminalKey = null;
    this.transition('print_result_unknown', this.errorMessage(error), undefined, false);
  }

  /** Повтор печати без банковской команды: касса проверяется, сервер выдаёт новый IdCommand. */
  protected override async retryAndPrint(
    execution: OrderFiscalExecutionFieldsFragment,
  ): Promise<OrderFieldsFragment | null> {
    this.transition('checking_readiness', 'Проверяем кассу.', execution);
    const ready = await this.captureLiveIdentity(execution);
    assertLiveKktReady(ready.kkt, execution.execution_target.timezone);
    const retried = await this.dependencies.services.retryReceipt({
      ...this.liveKkt.identityFor(execution),
      kkt: orderFiscalEquipmentVerification(ready).kkt,
    });

    return this.printAndFinalize(retried);
  }

  private async runPrepared(execution: OrderFiscalExecutionFieldsFragment): Promise<OrderFieldsFragment | null> {
    // Банковская команда уходит только из `prepared`. Повтор prepare с тем же ключом (второй
    // клик, ждавший очереди) отдаёт уже начатую операцию: тот же `IdCommand` KKMServer
    // выполнил бы заново после ошибки или перезапуска, поэтому её исход читает `GetRezult`.
    if (execution.operation.status !== OrderFiscalOperationStatus.Prepared) {
      return this.continueRecovery(execution, false);
    }

    const mapped = await this.mapPrepared(execution);

    if (mapped === null) return null;

    if (mapped.acquiringCommand !== null) {
      const refusal = await validateReceiptMarking(this.dependencies.adapter, execution);
      if (refusal !== null) {
        await this.failKnownPrePrint(execution, new Error(refusal), 'marking_rejected');
        this.transition('failed_before_print', refusal, execution);
        return null;
      }
    }

    const started = await this.dependencies.services.start(this.liveKkt.identityFor(execution));

    if (mapped.acquiringCommand !== null && mapped.acquiringDeviceKey !== null) {
      this.activeTerminalKey = mapped.acquiringDeviceKey;
      this.transition('waiting_payment', 'Передайте терминал клиенту.', started, true);
      let bankResult = await executeKkmSafely(() =>
        this.dependencies.adapter.execute(mapped.acquiringCommand as NonNullable<typeof mapped.acquiringCommand>, {
          deviceKey: mapped.acquiringDeviceKey as string,
          timeoutClass: 'acquiring',
        }),
      );
      this.activeTerminalKey = null;

      const knownBankFailure = bankResult.kind === 'error' && bankResult.error.outcome !== 'unknown';
      if ((bankResult.kind !== 'ok' || !bankResult.acquiring?.rrn) && !knownBankFailure) {
        bankResult =
          (await this.paymentCancellation.recheckPaymentResult(started, bankResult, mapped.acquiringDeviceKey)) ??
          bankResult;
      }

      if (bankResult.kind === 'error' && bankResult.error.kind === 'payment_declined') {
        await this.failKnown(started, bankResult, OrderFiscalFailureKind.PaymentDeclined);
        this.transition(
          'payment_declined',
          'Банк не пропустил оплату. Деньги не списаны — попробуйте ещё раз или другим способом.',
          started,
        );
        return null;
      }

      // Очередь Status=4 не разрешает повтор; нужен окончательный отказ или
      // доказанный сбой транспорта до отправки самой оплаты.
      if (bankResult.kind === 'error' && bankResult.error.outcome !== 'unknown') {
        const cancelled = bankResult.error.kind === 'user_cancelled';
        console.warn('[OrderFiscalizationController.definitive-not-run]', {
          operationId: started.operation.id,
          branch: 'card',
          resultKind: bankResult.kind,
        });
        await this.failKnown(
          started,
          bankResult,
          cancelled ? OrderFiscalFailureKind.PaymentCancelled : OrderFiscalFailureKind.KnownPrePrint,
        );
        if (cancelled) {
          // Сырой текст терминала (код, «сеансовые ключи») кассиру не нужен: он в подробностях и журнале.
          this.transition(
            'cancelled_on_terminal',
            'Деньги не списаны.',
            started,
            false,
            null,
            bankResult.error.vendorMessage,
          );
          return null;
        }
        this.transition('failed_before_print', resultOperatorMessage(bankResult, bankResult.error.message), started);
        return null;
      }

      if (bankResult.kind !== 'ok' || !bankResult.acquiring?.rrn) {
        await this.completeAmbiguous(started, bankResult);
        this.paymentCancellation.showUnconfirmedPayment(
          started,
          bankResult.kind === 'error' ? bankResult.error.vendorMessage : null,
        );
        return null;
      }

      const captured = await this.dependencies.services.complete({
        ...this.liveKkt.identityFor(started),
        result: {
          bank: toBankEvidenceInput(bankResult.acquiring),
          commandStatus: commandStatus(bankResult),
          outcome: OrderFiscalSettlementResultOutcome.PaymentCaptured,
        },
      });

      return this.printAndFinalize(captured.execution);
    }

    return this.printAndFinalize(started);
  }

  /**
   * Mapping отклоняет неподдержанный план уже после успешного prepare. Операция и
   * резерв баллов остались бы активными до истечения TTL, поэтому закрываем её
   * fail-мутацией сразу — повтор доступен без ожидания.
   */
  protected override async mapPrepared(
    execution: OrderFiscalExecutionFieldsFragment,
  ): Promise<KkmServerMappedReceiptPlan | null> {
    try {
      return mapFiscalExecutionReceipt(execution, this.dependencies.mapReceiptPlan);
    } catch (error) {
      console.error('[OrderFiscalizationController.mapping-unsupported]', {
        operationId: execution.operation.id,
        reason: this.errorMessage(error),
      });
      await this.failKnownPrePrint(execution, error);
      this.transition('failed_before_print', this.errorMessage(error), execution);

      return null;
    }
  }

  private async failKnownPrePrint(
    execution: OrderFiscalExecutionFieldsFragment,
    error: unknown,
    errorCode = 'mapping_unsupported',
  ): Promise<void> {
    try {
      await this.dependencies.services.fail({
        ...this.liveKkt.identityFor(execution),
        commandStatus: OrderFiscalKkmCommandStatus.NotRun,
        errorCode,
        errorMessage: this.errorMessage(error),
        failureKind: OrderFiscalFailureKind.KnownPrePrint,
      });
      this.dependencies.storage.clearPendingOperation(execution.operation.id);
    } catch (failError) {
      console.error('[OrderFiscalizationController.mapping-fail-mutation-failed]', {
        operationId: execution.operation.id,
        reason: this.errorMessage(failError),
      });
    }
  }

  private async pollAndResume(execution: OrderFiscalExecutionFieldsFragment): Promise<OrderFieldsFragment | null> {
    const polled = await this.pollPrepared(execution);
    if (polled === null) return null;
    const { hasCard, result } = polled;

    if (hasCard) {
      if (result.kind === 'ok' && result.acquiring !== null) {
        const captured = await this.dependencies.services.complete({
          ...this.liveKkt.identityFor(execution),
          result: {
            bank: toBankEvidenceInput(result.acquiring),
            commandStatus: OrderFiscalKkmCommandStatus.Ok,
            outcome: OrderFiscalSettlementResultOutcome.PaymentCaptured,
          },
        });
        return this.printAndFinalize(captured.execution);
      }

      if (isProvenNotRun(result)) {
        console.warn('[OrderFiscalizationController.definitive-not-run]', {
          operationId: execution.operation.id,
          branch: 'card_recovery',
          resultKind: result.kind,
        });
        await this.failKnown(execution, result, OrderFiscalFailureKind.KnownPrePrint);
        this.transition(
          'failed_before_print',
          resultOperatorMessage(result, 'Терминал не выполнил команду. Деньги не списаны — попробуйте ещё раз.'),
          execution,
        );
        return null;
      }

      await this.completeAmbiguous(execution, result);
      this.paymentCancellation.showUnconfirmedPayment(
        execution,
        result.kind === 'error' ? result.error.vendorMessage : null,
      );
      return null;
    }

    return this.finalizePrintResult(execution, result);
  }

  private async printAndFinalize(execution: OrderFiscalExecutionFieldsFragment): Promise<OrderFieldsFragment | null> {
    const result = await this.executeReceipt(execution, 'Касса печатает чек.', this.dependencies.mapReceiptPlan);
    return this.finalizePrintResult(execution, result);
  }

  private async finalizePrintResult(
    execution: OrderFiscalExecutionFieldsFragment,
    result: KkmExecutionResult,
  ): Promise<OrderFieldsFragment | null> {
    const { kktShiftClosedExternally, unreceipted } = await this.completePrintFailure(
      execution,
      result,
      OrderFiscalSettlementResultOutcome.PaymentCapturedUnfiscalized,
      (input) => this.dependencies.services.complete(input),
    );

    if (result.kind !== 'ok') {
      if (unreceipted !== null) {
        if (this.showReceiptRetry(unreceipted)) return null;
        if (kktShiftClosedExternally) {
          this.resolvableExecution = unreceipted;
          this.transition(
            'payment_without_receipt',
            'Деньги списаны, чек не вышел, а смена на кассе была закрыта извне. Откройте смену и нажмите «Напечатать чек».',
            unreceipted,
            false,
            'reopen',
          );
          return null;
        }
        this.showResolveReceipt(
          unreceipted,
          resultOperatorMessage(result, 'Деньги получены, чек не подтверждён. Нажмите «Напечатать чек».'),
        );
        return null;
      }

      if (isProvenNotRun(result)) {
        console.warn('[OrderFiscalizationController.definitive-not-run]', {
          operationId: execution.operation.id,
          branch: 'kkt',
          resultKind: result.kind,
        });
        await this.failKnown(execution, result, OrderFiscalFailureKind.KnownPrePrint);
        this.transition(
          'failed_before_print',
          resultOperatorMessage(
            result,
            kktShiftClosedExternally
              ? 'Смена на кассе была закрыта извне. Смена в Морсе продолжается.'
              : (markingRefusalMessage(result) ??
                  (result.kind === 'error'
                    ? result.error.message
                    : 'Касса не выполнила команду. Чек не напечатан — попробуйте ещё раз.')),
          ),
          execution,
          false,
          kktShiftClosedExternally ? 'reopen' : null,
        );
        return null;
      }

      const unresolved = await this.completeAmbiguous(execution, result);
      if (kktShiftClosedExternally) {
        this.resolvableExecution = unresolved;
        this.transition(
          'print_result_unknown',
          'Смена на кассе была закрыта извне, а результат чека неясен. Откройте смену и нажмите «Напечатать чек».',
          unresolved,
          false,
          'reopen',
        );
        return null;
      }
      this.showResolveReceipt(
        unresolved,
        resultOperatorMessage(result, 'Непонятно, вышел ли чек. Нажмите «Напечатать чек»: Mors проверит кассу.'),
      );
      return null;
    }

    const settlementResult = fiscalizedResult(result, execution.execution_target.timezone);
    this.unownedCodes = settlementResult.unownedMarkingCodes ?? [];
    const completeInput: CompleteOrderFiscalSettlementInput = {
      ...this.liveKkt.identityFor(execution),
      result: settlementResult,
    };
    return this.savePrintedReceipt(execution, completeInput);
  }

  private async completeAmbiguous(
    execution: OrderFiscalExecutionFieldsFragment,
    result: KkmExecutionResult,
  ): Promise<OrderFiscalExecutionFieldsFragment> {
    const completed = await this.dependencies.services.complete({
      ...this.liveKkt.identityFor(execution),
      result: {
        commandStatus: commandStatus(result),
        ...fiscalResultErrorFields(result, execution.operation.id),
        outcome: OrderFiscalSettlementResultOutcome.Ambiguous,
      },
    });

    return completed.execution;
  }

  private async failKnown(
    execution: OrderFiscalExecutionFieldsFragment,
    result: KkmExecutionResult,
    failureKind: OrderFiscalFailureKind,
  ): Promise<void> {
    await this.dependencies.services.fail({
      ...this.liveKkt.identityFor(execution),
      commandStatus: commandStatus(result),
      ...fiscalResultErrorFields(result, execution.operation.id),
      failureKind,
    });
    this.dependencies.storage.clearPendingOperation(execution.operation.id);
  }

  /**
   * `selectKktDevice` требует ровно одного кандидата, поэтому без явного
   * `kktDeviceId` любая локация с несколькими настроенными ККТ не доходит
   * до prepare — ни в checkout, ни в recovery.
   */
  protected override async handshakeBoundKkt(kktDeviceId: string | null | undefined) {
    console.warn('[OrderFiscalizationController.handshake]', {
      hasBoundKkt: Boolean(kktDeviceId),
    });

    return this.dependencies.adapter.handshake(kktDeviceId ? { kktDeviceId } : {});
  }

  private async captureLiveIdentity(execution: OrderFiscalExecutionFieldsFragment): Promise<KkmHandshakeReady> {
    const ready = requireReadyHandshake(await this.handshakeBoundKkt(execution.execution_target.kkt_device_id));
    assertExecutionTarget(execution, ready);
    this.liveKkt.capture(ready);
    return ready;
  }

  protected override transition(
    phase: OrderFiscalizationPhase,
    message: string | null = null,
    execution?: OrderFiscalExecutionFieldsFragment,
    cancellable = false,
    shiftAction: OrderFiscalizationState['shiftAction'] = null,
    technicalDetails: string | null = this.paymentCancellation.review?.technicalDetails ?? null,
  ): void {
    this.stateValue = {
      amount: execution ? displayFiscalizationAmount(phase, execution) : this.stateValue.amount,
      canCancelPayment:
        phase === 'payment_unconfirmed' ||
        (phase === 'payment_without_receipt' && this.retryableExecution?.terminal_transaction_id != null),
      canRetryReceipt: this.retryableExecution !== null,
      canResolveReceipt:
        this.resolvableExecution !== null &&
        !this.receiptQuestionOpen &&
        (phase === 'payment_without_receipt' || phase === 'print_result_unknown' || phase === 'kkt_unavailable'),
      receiptQuestion: this.receiptQuestionOpen && phase === 'print_result_unknown',
      cashToReturn: phase === 'payment_cancelled' ? this.paymentCancellation.cashToReturn : 0,
      canReset:
        RESETTABLE_PHASES.has(phase) ||
        (phase === 'payment_cancelled' && this.paymentCancellation.cancelledOrderStatus === OrderStatus.Draft),
      cancellable,
      message,
      phase,
      shiftAction,
      terminalName: execution?.operation.workstation.name ?? this.stateValue.terminalName,
      technicalDetails,
      unownedMarkingCodes: this.unownedCodes,
    };
    this.emitState();
  }
}
